import { ALL_FACES } from '../cards';
import { enemyBackRow, onBoard, onBoardPawns, opponent, sameSquare } from '../board';
import type { MoveOption } from '../movement';
import type { Face, MatchEvent, MatchState, Pawn, Side, Square, TrickTarget } from '../types';
import { returnPawn, type Draw } from './flow';
import { immuneIn, omenAt } from './hooks';
import type { TrickId } from './ids';
import {
  L,
  addBrass,
  addSideStatus,
  hasStatus,
  setStatus,
  sideStatus,
  untilOwnersNextTurn,
} from './state';

/**
 * Tricks (LEDGER.MD §12.2, docs/SHOP_DESIGN.md §10.7): cards with a printed face that match the
 * pile and count as a link, for the link limit, the multiplier and patterns, but move nothing.
 * The chain pawn stays where it is and the Trick does its effect instead.
 */

type Events = MatchEvent[];

/** A Trick's link: no landing, no capture. */
export const STILL: MoveOption = {
  landings: [],
  captures: [],
  captureAt: [],
  move: { type: 'still' },
};

const occupied = (s: MatchState, sq: Square): boolean =>
  Object.values(s.pawns).some((p) => p.status === 'onBoard' && sameSquare(p.square, sq)) ||
  !!omenAt(s, sq, 'pillar');

/** An uncrowned pawn may not be put on its crowning row by anything but its own move. */
const wouldSitOnCrownRow = (pawn: Pawn, sq: Square): boolean =>
  !pawn.crowned && sq.y === enemyBackRow(pawn.side);

/**
 * What a Trick may be played on with this chain pawn: `[{}]` for a Trick that names nothing, and
 * none at all when it has nothing to work on. `faceOk` says whether a face may begin the chain
 * (Single File), for Chalk Line.
 */
export function trickTargets(
  s: MatchState,
  side: Side,
  trick: TrickId,
  chainPawn: Pawn,
  faceOk: (f: Face) => boolean,
): TrickTarget[] {
  const here = chainPawn.square;
  if (!here) return [];
  const enemies = onBoardPawns(s.pawns, opponent(side));
  switch (trick) {
    case 'reverse':
    case 'gag':
    case 'draw-two':
    case 'tithe':
      return [{}];
    case 'chalk-line':
      return ALL_FACES.filter(faceOk).map((face) => ({ face }));
    case 'swap':
      return onBoardPawns(s.pawns, side)
        .filter(
          (p) =>
            p.id !== chainPawn.id &&
            !!p.square &&
            !hasStatus(s, p.id, 'pinned') &&
            !wouldSitOnCrownRow(chainPawn, p.square) &&
            !wouldSitOnCrownRow(p, here),
        )
        .map((p) => ({ pawnId: p.id }));
    case 'shove':
      return enemies
        .filter((p) => {
          const to = shoveSquare(here, p);
          return !!to && !occupied(s, to) && !immuneIn(s, p, side) && !wouldSitOnCrownRow(p, to);
        })
        .map((p) => ({ pawnId: p.id }));
    case 'hex':
      return enemies.map((p) => ({ pawnId: p.id }));
    case 'requiem':
      return Object.values(s.pawns)
        .filter((p) => p.side === side && p.status !== 'onBoard' && !occupied(s, p.startSquare))
        .map((p) => ({ pawnId: p.id }));
  }
}

/** Where a Shove pushes an enemy next to the chain pawn: one square straight away from it. */
export function shoveSquare(from: Square, target: Pawn): Square | null {
  const at = target.square;
  if (!at) return null;
  const dx = at.x - from.x;
  const dy = at.y - from.y;
  if (Math.max(Math.abs(dx), Math.abs(dy)) !== 1) return null;
  const to = { x: at.x + dx, y: at.y + dy };
  return onBoard(to.x, to.y) ? to : null;
}

export const sameTrickTarget = (a: TrickTarget, b: TrickTarget): boolean =>
  a.pawnId === b.pawnId &&
  (a.face ? !!b.face && a.face.color === b.face.color && a.face.number === b.face.number : !b.face);

/** Does a Trick's effect, once its card is on the pile and its link recorded. */
export function applyTrick(
  s: MatchState,
  side: Side,
  cardId: string,
  chainPawnId: string,
  trick: TrickId,
  target: TrickTarget,
  ev: Events,
  draw: Draw,
): void {
  const other = opponent(side);
  ev.push({ type: 'TrickPlayed', side, cardId, trick, target });
  switch (trick) {
    case 'reverse':
      if (!sideStatus(s, other, 'reversed')) addSideStatus(s, other, 'reversed', 1);
      break;
    case 'gag':
      if (!sideStatus(s, other, 'gagged')) addSideStatus(s, other, 'gagged', 1);
      break;
    case 'swap': {
      const a = s.pawns[chainPawnId];
      const b = target.pawnId ? s.pawns[target.pawnId] : undefined;
      if (!a?.square || !b?.square) break;
      const sa = a.square;
      const sb = b.square;
      s.pawns[a.id] = { ...a, square: sb };
      s.pawns[b.id] = { ...b, square: sa };
      ev.push({ type: 'PawnMoved', side, pawnId: a.id, from: sa, to: sb, cause: 'swap' });
      ev.push({ type: 'PawnMoved', side, pawnId: b.id, from: sb, to: sa, cause: 'swap' });
      break;
    }
    case 'shove': {
      const chain = s.pawns[chainPawnId];
      const p = target.pawnId ? s.pawns[target.pawnId] : undefined;
      const to = chain?.square && p ? shoveSquare(chain.square, p) : null;
      if (!p?.square || !to) break;
      const from = p.square;
      s.pawns[p.id] = { ...p, square: to };
      ev.push({ type: 'PawnMoved', side: other, pawnId: p.id, from, to, cause: 'shove' });
      break;
    }
    case 'chalk-line':
      // The named face is the link's face (the pile's top and the patterns): nothing more.
      break;
    case 'draw-two':
      draw(s, side);
      draw(s, side);
      break;
    case 'hex':
      if (target.pawnId && s.pawns[target.pawnId]?.status === 'onBoard')
        setStatus(s, target.pawnId, { kind: 'pinned', turns: untilOwnersNextTurn(s, other) }, ev);
      break;
    case 'requiem':
      if (target.pawnId) returnPawn(s, target.pawnId, 'returned', ev);
      break;
    case 'tithe':
      addBrass(s, side, 2, 'tithe', ev);
      break;
  }
}

/** Reverse and Gag last the affected side's next turn: they end when that turn does. */
export function endTrickStatuses(s: MatchState, side: Side): void {
  if (!s.ledger) return;
  const l = L(s);
  const list = l.sideStatuses[side];
  if (!list.some((x) => x.kind === 'reversed' || x.kind === 'gagged')) return;
  s.ledger = {
    ...l,
    sideStatuses: {
      ...l.sideStatuses,
      [side]: list.filter((x) => x.kind !== 'reversed' && x.kind !== 'gagged'),
    },
  };
}
